/**
 * OperationsPage — tabbed command center.
 *
 * Four tabs (order): Job Board | Dispatch Board | Map View | Completed.
 *   - Job Board — the job queue (full width). No map here.
 *   - Dispatch Board — driver/job board.
 *   - Map View — the live driver map (full width). The ONLY tab that shows the map.
 *   - Completed — completed-jobs grid.
 *
 * Map mount strategy (Quick 260619-map #2; DEC-057 — Mapbox bills per map LOAD):
 *   The DriverMap is rendered UNCONDITIONALLY (it stays mounted for the whole
 *   Operations session) and is shown/hidden with CSS `display`, never by
 *   mounting/unmounting it on tab switch. DriverMap acquires the single shared
 *   `mapboxgl.Map` (MapProvider, DEC-057) exactly once on mount; that instance is
 *   never `.remove()`d while Operations is open. Toggling `display` costs zero map
 *   loads, whereas remounting DriverMap on every Map-View visit would re-acquire
 *   (and, worst case, reload) the map. Driver-position polling is gated to the
 *   Map View tab via the `active` prop so we don't poll while the map is hidden.
 *
 * Spec: UI-SPEC §2; DISPATCH-01/05; §4 (Design System — Maps); DEC-057; OPEN-006.
 */

import * as React from 'react';
import {
  Plus,
  Map as MapIcon,
  LayoutGrid,
  ListChecks,
  ClipboardList,
  ClipboardCheck,
} from 'lucide-react';
import { JobQueue } from '../features/jobs/JobQueue';
import { DriverMap } from '../features/drivers/DriverMap';
import { NewJobModal } from '../features/jobs/NewJobModal';
import { useRejectionPoller } from '../features/notifications/useRejectionPoller';
import { useJobs } from '../features/jobs/use-jobs';
import { DispatchBoard } from '../features/board/DispatchBoard';
import { CompletedJobsTab } from '../features/jobs/CompletedJobsTab';
import { InReviewTab } from '../features/review/InReviewTab';
import { useInReviewCount } from '../features/review/use-inreview';
import { computeMetrics } from '../features/board/board-utils';
import { useUserProfile } from '../lib/use-user-timezone';
import { useFeatureEnabled } from '../lib/use-features';
import { GatedFeaturePrompt, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
// REVIEW_ROLES — roles holding REVIEW_TICKET (§6): who can see the InReview queue tab.
import { REVIEW_ROLES } from '../lib/role-gates';
import { useUrlParam } from '../lib/url-state';

type OpsTab = 'jobs' | 'board' | 'map' | 'completed' | 'review';
const OPS_TABS: readonly OpsTab[] = ['jobs', 'board', 'map', 'completed', 'review'];

export function OperationsPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data } = useJobs(apiOpts, {});
  const jobs = React.useMemo(() => data?.jobs ?? [], [data]);

  // Rejection poller — fires toasts + bell increments while Operations is mounted
  useRejectionPoller(jobs);

  // Metric buckets live in THIS header bar so they show on every tab. The other
  // three derive from `jobs`; completed-today needs its own query.
  // OPEN-057 — ask the SERVER how many completed today; do not fetch a page of
  // jobs and count it here. The old approach could not be right: the list is
  // ordered by created_at DESC, so a job created weeks ago and completed today
  // fell outside the page silently, and it filtered on the transient COMPLETED
  // status, so a job completed this morning and advanced to TO_BE_INVOICED this
  // afternoon vanished from the tile. DEC-066: the day is the DISPATCHER's
  // local day, so send the browser timezone rather than letting the server
  // assume one.
  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone !== ''
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Chicago';
  const completedCountQuery = useApiQuery<{ count: number }>(
    apiOpts,
    `/v1/jobs/completed/count?tz=${encodeURIComponent(browserTz)}`,
    { queryKey: ['jobs', { board: 'completed-count', tz: browserTz }], staleTime: 60_000 },
  );
  const metrics = React.useMemo(
    () => computeMetrics(jobs, completedCountQuery.data?.count ?? 0),
    [jobs, completedCountQuery.data],
  );

  const { data: profileData } = useUserProfile();
  // §58 PR 3 (DEC-366 Q7): the Map View is the LIVE_MAP feature. null while the profile
  // loads: render nothing, so a hauler without the feature never causes a Mapbox load.
  const liveMap = useFeatureEnabled('LIVE_MAP');

  // InReview tab — visible only to REVIEW_TICKET roles (§6). Badge = queue count.
  const role = profileData?.profile.role ?? null;
  const canReview = role !== null && REVIEW_ROLES.has(role);
  const inreviewCount = useInReviewCount().data?.count ?? 0;

  // Job Board is the default landing tab. OPEN-191 — the tab lives in `?tab=`, so Back from a
  // detail page, a refresh and a shared link land on it. Switching tab closes the In Review
  // ticket (`?jobId=`) but keeps the queue's filters and each table's sort. OPEN-144 PR 3 —
  // `?tab=review&jobId=` (the job modal's "2nd ticket parked" badge) opens that ticket in In
  // Review: InReviewTab reads `jobId` itself, so the link no longer has to be cleared.
  const [tabParam, setTab] = useUrlParam('tab', 'jobs', ['jobId']);
  const urlTab = OPS_TABS.find((t) => t === tabParam) ?? 'jobs';
  // A non-reviewer at ?tab=review lands on the Job Board once the role is known; while the
  // profile loads the tab stays put (and shows nothing), so a reviewer's link is not lost.
  const tab: OpsTab = urlTab === 'review' && role !== null && !canReview ? 'jobs' : urlTab;
  const [newJobOpen, setNewJobOpen] = React.useState(false);

  const mapActive = tab === 'map';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Toolbar: tabs (left) + New Job (right) */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
          flexShrink: 0,
        }}
      >
        {/* Tab bar — order: Job Board | Dispatch Board | Map View | Completed */}
        <div role="tablist" aria-label="Operations view" style={{ display: 'flex', gap: '4px' }}>
          <TabButton
            active={tab === 'jobs'}
            onClick={(): void => {
              setTab('jobs');
            }}
            icon={<ClipboardList size={15} aria-hidden="true" />}
            label="Job Board"
          />
          <TabButton
            active={tab === 'board'}
            onClick={(): void => {
              setTab('board');
            }}
            icon={<LayoutGrid size={15} aria-hidden="true" />}
            label="Dispatch Board"
          />
          <TabButton
            active={tab === 'map'}
            onClick={(): void => {
              setTab('map');
            }}
            icon={<MapIcon size={15} aria-hidden="true" />}
            label="Map View"
          />
          <TabButton
            active={tab === 'completed'}
            onClick={(): void => {
              setTab('completed');
            }}
            icon={<ListChecks size={15} aria-hidden="true" />}
            label="Completed"
          />
          {canReview && (
            <TabButton
              active={tab === 'review'}
              onClick={(): void => {
                setTab('review');
              }}
              icon={<ClipboardCheck size={15} aria-hidden="true" />}
              label="InReview"
              badge={inreviewCount}
            />
          )}
        </div>

        {/* Compact metric buckets, always visible on every tab */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            flex: 1,
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <HeaderMetric label="Unassigned" value={metrics.unassigned} />
          <HeaderMetric label="Not Started" value={metrics.notStarted} />
          <HeaderMetric label="In Progress" value={metrics.inProgress} />
          <HeaderMetric label="Completed Today" value={metrics.completedToday} />
        </div>

        <button
          type="button"
          onClick={(): void => {
            setNewJobOpen(true);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            minHeight: '36px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: 'var(--radius-pill)',
            backgroundColor: 'var(--color-cta)',
            color: 'var(--color-on-cta)',
            cursor: 'pointer',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New Job
        </button>
      </div>

      <NewJobModal open={newJobOpen} onOpenChange={setNewJobOpen} />

      {/* Content area — exactly one tab's content is visible at a time. The DriverMap
          is the exception: it is ALWAYS mounted (display-toggled) so the shared
          Mapbox instance is never reloaded on tab switch (DEC-057 / #2). */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {tab === 'jobs' && (
          <div style={{ height: '100%', overflow: 'hidden' }}>
            <JobQueue />
          </div>
        )}
        {tab === 'board' && (
          <div style={{ height: '100%', minHeight: 0 }}>
            <DispatchBoard />
          </div>
        )}
        {tab === 'completed' && (
          <div style={{ height: '100%', minHeight: 0 }}>
            <CompletedJobsTab />
          </div>
        )}
        {tab === 'review' && canReview && (
          <div style={{ height: '100%', minHeight: 0 }}>
            <InReviewTab />
          </div>
        )}
        {/* Map View — NEVER unmounted (DEC-057): hidden with display:none when its
            tab is inactive so the single shared map load is preserved across tab
            switches. Driver-position polling is paused while inactive (`active`). */}
        <div style={{ height: '100%', display: mapActive ? 'block' : 'none' }}>
          {liveMap === true && <DriverMap active={mapActive} />}
          {liveMap === false && <GatedFeaturePrompt />}
        </div>
      </div>
    </div>
  );
}

/** Header metric: prominent bold number + small dim uppercase label, inline. */
function HeaderMetric({ label, value }: { label: string; value: number }): React.JSX.Element {
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px', whiteSpace: 'nowrap' }}
    >
      <span
        style={{
          fontSize: '22px',
          fontWeight: 900,
          lineHeight: 1,
          color: 'var(--color-text-primary)',
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: 'var(--color-text-tertiary)',
          opacity: 0.8,
        }}
      >
        {label}
      </span>
    </span>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        minHeight: '32px',
        fontSize: '13px',
        fontWeight: 600,
        borderRadius: 'var(--radius-pill)',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: active ? 'var(--color-accent-blue)' : 'transparent',
        color: active ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
      }}
    >
      {icon}
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge.toString()} tickets in review`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '18px',
            height: '18px',
            padding: '0 5px',
            fontSize: '11px',
            fontWeight: 700,
            borderRadius: '9px',
            backgroundColor: active ? 'rgba(20, 22, 28, 0.18)' : 'var(--color-accent-blue)',
            color: 'var(--color-on-accent)',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
